import { useAuth } from "../contexts/AuthContext";
import { useMeetingStatuses } from "../hooks/useMeetingStatuses";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Button } from "../components/ui/button";
import { Loader2, RotateCcw, ExternalLink } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useToast } from "../hooks/use-toast";
import { supabase } from "../lib/supabase";
import { ProcessStatus, ProcessType } from "../types/process";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import React from "react";

export default function Status() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: meetingStatuses, isLoading, error, refetch } = useMeetingStatuses(user?.id);
  const { toast } = useToast();

  const getProgressValue = (statuses: Record<string, ProcessStatus>) => {
    const total = Object.keys(statuses).length;
    const completed = Object.values(statuses).filter(s => s === 'completed').length;
    return (completed / total) * 100;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  };

  const handleRetry = async (meetingId: string) => {
    try {
      const { error } = await supabase
        .from('process_statuses')
        .update({
          status: 'queued',
          updated_at: new Date().toISOString(),
          error_message: null
        })
        .eq('meeting_id', meetingId)
        .eq('status', 'error');

      if (error) throw error;

      await refetch();
      
      toast({
        title: "Processing restarted",
        description: "The meeting is being processed again.",
      });
    } catch (error) {
      console.error('Error retrying process:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to retry processing. Please try again.",
      });
    }
  };

  const handleViewMeeting = (meetingId: string) => {
    navigate(`/app/meeting/${meetingId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-4xl font-bold tracking-tight">Processing Status</h1>
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-4xl font-bold tracking-tight">Processing Status</h1>
        <Card className="p-6">
          <div className="text-red-500">
            Failed to load status information. Please try again.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <h1 className="text-4xl font-bold tracking-tight">Processing Status</h1>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[250px]">MEETING</TableHead>
              <TableHead>PROGRESS</TableHead>
              <TableHead className="w-[120px]">UPLOAD</TableHead>
              <TableHead className="w-[120px]">TRANSCRIPTION</TableHead>
              <TableHead className="w-[120px]">ANALYSIS</TableHead>
              <TableHead className="w-[200px]">ERROR</TableHead>
              <TableHead className="w-[100px]">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {meetingStatuses && meetingStatuses.length > 0 ? (
              meetingStatuses.map(({ meeting, statuses, error_messages }) => (
                <TableRow key={meeting.id} className="group">
                  <TableCell className="font-medium">
                    <div className="space-y-1">
                      <div className="font-semibold group-hover:text-primary cursor-pointer" 
                           onClick={() => handleViewMeeting(meeting.id)}>
                        {meeting.title || "Untitled Meeting"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(meeting.created_at)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Progress value={getProgressValue(statuses)} className="h-2" />
                      <div className="text-xs text-muted-foreground">
                        {Math.round(getProgressValue(statuses))}% complete
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={statuses.upload} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={statuses.transcription} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={statuses.analysis} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {error_messages ? Object.values(error_messages)[0] : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {Object.values(statuses).includes('error') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRetry(meeting.id)}
                          className="h-8 w-8 p-0"
                        >
                          <RotateCcw className="h-4 w-4" />
                          <span className="sr-only">Retry</span>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewMeeting(meeting.id)}
                        className="h-8 w-8 p-0"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span className="sr-only">View Meeting</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                  No meetings found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}